import { segment, track, identify, adPartnersTo } from '@mc/track-event'

type CommonFields = {
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  organizationName: string
  jobTitle: string
  seats?: number
  useCase: string
  whenAreYouLookingToPurchase?: string
  gdprOptIn: boolean
  employeeSize?: string
}

export const trackFormSubmitted = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  organizationName,
  jobTitle,
  seats,
  useCase,
  whenAreYouLookingToPurchase,
  gdprOptIn,
  revenueFields,
  isLoggedIn,
}: CommonFields & {
  revenueFields?: {
    likelihoodOfOpportunity: number
    winRate: number
    avgDealSize: number
    revenue: number
  } | null
  isLoggedIn: boolean
}) => {
  track(
    segment.EventTypes.FOR_BUSINESSES_LEAD_FORM_SUBMITTED,
    {
      first_name: firstName,
      last_name: lastName,
      email_address: email,
      phone_number: phoneNumber,
      organization_name: organizationName,
      job_title: jobTitle,
      group_size: seats,
      use_case: useCase,
      when_are_you_looking_to_purchase: whenAreYouLookingToPurchase,
      gdpr_opt_in: gdprOptIn,
      ...revenueFields,
    },
    {
      integrations: {
        ...adPartnersTo(false),
        Hotjar: false,
        'Google Tag Manager': !isLoggedIn,
      },
    },
  )
}

export const trackFormSuccess = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  organizationName,
  jobTitle,
  seats,
  useCase,
  whenAreYouLookingToPurchase,
  gdprOptIn,
}: CommonFields) =>
  track(segment.EventTypes.FOR_BUSINESSES_LEAD_FORM_SUCCESS, {
    first_name: firstName,
    last_name: lastName,
    email_address: email,
    phone_number: phoneNumber,
    organization_name: organizationName,
    job_title: jobTitle,
    group_size: seats,
    use_case: useCase,
    when_are_you_looking_to_purchase: whenAreYouLookingToPurchase,
    gdpr_opt_in: gdprOptIn,
  })

export const trackFormFail = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  organizationName,
  jobTitle,
  seats,
  useCase,
  whenAreYouLookingToPurchase,
  gdprOptIn,
  error,
}: CommonFields & {
  error: string
}) =>
  track(segment.EventTypes.FOR_BUSINESSES_LEAD_FORM_FAIL, {
    first_name: firstName,
    last_name: lastName,
    email_address: email,
    phone_number: phoneNumber,
    organization_name: organizationName,
    job_title: jobTitle,
    group_size: seats,
    use_case: useCase,
    when_are_you_looking_to_purchase: whenAreYouLookingToPurchase,
    gdpr_opt_in: gdprOptIn,
    error,
  })

export const trackEnterpriseSalesLeadSuccess = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  organizationName,
  jobTitle,
  seats,
  useCase,
  whenAreYouLookingToPurchase,
  gdprOptIn,
}: CommonFields) =>
  track(segment.EventTypes.ENTERPRISE_SALES_LEAD_SUCCESS, {
    first_name: firstName,
    last_name: lastName,
    email_address: email,
    phone_number: phoneNumber,
    organization_name: organizationName,
    job_title: jobTitle,
    group_size: seats,
    use_case: useCase,
    when_are_you_looking_to_purchase: whenAreYouLookingToPurchase,
    gdpr_opt_in: gdprOptIn,
  })

export const trackEnterpriseSalesLeadFail = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  organizationName,
  jobTitle,
  seats,
  useCase,
  whenAreYouLookingToPurchase,
  gdprOptIn,
  error,
}: CommonFields & {
  error: string
}) =>
  track(segment.EventTypes.ENTERPRISE_SALES_LEAD_FAIL, {
    first_name: firstName,
    last_name: lastName,
    email_address: email,
    phone_number: phoneNumber,
    organization_name: organizationName,
    job_title: jobTitle,
    group_size: seats,
    use_case: useCase,
    when_are_you_looking_to_purchase: whenAreYouLookingToPurchase,
    gdpr_opt_in: gdprOptIn,
    error,
  })

export const identifyFormSubmitted = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  organizationName,
  jobTitle,
  employeeSize,
  useCase,
  whenAreYouLookingToPurchase,
}: Omit<CommonFields, 'gdprOptIn'>) => {
  const options = {
    integrations: {
      Pardot: true,
    },
  }
  identify(
    null,
    {
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phoneNumber,
      organization_name: organizationName,
      job_title: jobTitle,
      group_size: employeeSize,
      use_case: useCase,
      when_are_you_looking_to_purchase: whenAreYouLookingToPurchase,
    },
    options,
  )
}
